/**
 * CancelBookingDialog Component
 * Dialog for cancelling a booking with required reason
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle } from 'lucide-react';

export default function CancelBookingDialog({
  open,
  onOpenChange,
  booking,
  cancellationReason,
  onReasonChange,
  onConfirm,
  onCancel
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-glass-border max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-500">
            <AlertTriangle className="w-5 h-5" />
            Cancel Booking
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to cancel this booking? This will remove the date from your availability and notify the client.
          </DialogDescription>
        </DialogHeader>
        
        {booking && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 mb-4">
            <p className="font-medium">{booking.client_name}</p>
            <p className="text-sm text-muted-foreground">
              {booking.event_type} • {booking.event_date}
            </p>
          </div>
        )}
        
        <div className="space-y-2">
          <Label htmlFor="cancellation-reason" className="text-red-400">
            Reason for Cancellation <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="cancellation-reason"
            value={cancellationReason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder="Please provide a reason for cancelling this booking..."
            className="bg-muted/20 border-glass-border min-h-[100px]"
            data-testid="cancellation-reason-input"
          />
        </div>
        
        <DialogFooter className="gap-2 mt-4">
          <Button
            variant="outline"
            onClick={onCancel}
            className="border-glass-border"
            data-testid="cancel-dialog-close-btn"
          >
            Keep Booking
          </Button>
          <Button
            onClick={onConfirm}
            className="bg-red-500 hover:bg-red-600 text-white"
            disabled={!cancellationReason?.trim()}
            data-testid="confirm-cancel-btn"
          >
            Cancel Booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
